import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { VideoGalleryRemoteParticipant } from "@azure/communication-react";
import { useCallAdapter } from "live-lesson/shared/feature-call-ui-react";
import { PcpGalleryPagerProps } from "../react/components/PcpGalleryPager";


const isUnmuted = (participant: VideoGalleryRemoteParticipant) => !participant.isMuted;
const hasRaisedHand = (participant: VideoGalleryRemoteParticipant) => participant.raisedHand !== undefined;
const isUnmutedOrRaisedHand = (participant: VideoGalleryRemoteParticipant) => isUnmuted(participant) || hasRaisedHand(participant);

const relevantPropertiesChanged = (prevParticipants: VideoGalleryRemoteParticipant[], nextParticipants: VideoGalleryRemoteParticipant[]) => {
    const prevParticipantsMap = new Map(prevParticipants.map(p => [p.userId, p]));
    const nextParticipantsMap = new Map(nextParticipants.map(p => [p.userId, p]));

    if (prevParticipantsMap.size !== nextParticipantsMap.size) return true;

    for (const [userId, prevParticipant] of prevParticipantsMap) {
        const nextParticipant = nextParticipantsMap.get(userId);
        if (
            !nextParticipant
            || prevParticipant.isSpeaking !== nextParticipant.isSpeaking
            || prevParticipant.isMuted !== nextParticipant.isMuted
            || prevParticipant.raisedHand !== nextParticipant.raisedHand
            || prevParticipant.videoStream?.id !== nextParticipant.videoStream?.id
        ) {
            return true;
        }
    }
    return false;
};


const useHasChanged = <T>(val: T) => {
    const prevVal = usePrevious(val)
    return prevVal !== val
}

const usePrevious = <T>(value: T): T | undefined => {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};


const useMockedParticipants = () => {
    const [mockedParticipants, setMockedParticipants] = useState<VideoGalleryRemoteParticipant[]>([]);
     
    // @ts-ignore
    window['addMockedParticipants'] = useCallback((numParticipants: number) => {
        setMockedParticipants(prev => {
            const newParticipants = Array.from({ length: numParticipants }, (_, i) => ({
                    userId: `mocked-${prev.length + i}`,
                    displayName: `Mocked Participant ${prev.length + i}`,
                    isMuted: true,
                    isSpeaking: false,
                    videoStream: undefined,
                    raisedHand: undefined,
                    state: 'Connected',
                } as VideoGalleryRemoteParticipant));
            return [...prev, ...newParticipants];
        });
    }, []);

    return mockedParticipants;
}


export const useParticipantsPagination = (
    participantsPerPage: number,
    remoteParticipants: VideoGalleryRemoteParticipant[]
) => {
    const [galleryPage, setGalleryPage] = useState(0);
    const [galleryNumPages, setGalleryNumPages] = useState(1);
    const hasMultiplePages = useMemo(() => galleryNumPages > 1, [galleryNumPages]);

    const adapter = useCallAdapter();

    const goToPage = useCallback((nextPage: number) => {
        if (nextPage >= galleryNumPages) {
            setGalleryPage(0);
        } else {
            setGalleryPage(nextPage);
        }
    }, [galleryPage, galleryNumPages]);

    const goToPrevPage = useCallback(() => goToPage(galleryPage - 1), [galleryNumPages, galleryPage]);
    const goToNextPage = useCallback(() => goToPage(galleryPage + 1), [galleryNumPages, galleryPage]);

    const hasPrevPage = useMemo(() => galleryPage > 0, [galleryPage]);
    const hasNextPage = useMemo(() => galleryPage < galleryNumPages - 1, [galleryPage, galleryNumPages]);


    // mock participants for testing the gallery/pagination without joining real users
    const mockedParticipants = useMockedParticipants();

    const [remoteParticipantsInternal, setRemoteParticipantsInternal] = useState<VideoGalleryRemoteParticipant[]>([]);
    useEffect(() => {
        // console.log('[change detection test]: remoteParticipants changed', remoteParticipants);
        setRemoteParticipantsInternal(prevState => {
            if (relevantPropertiesChanged(prevState, remoteParticipants)) {
                // console.log('[change detection test]: 1) actual change', prevState, remoteParticipants);
                return remoteParticipants;
            }

            // console.log('[change detection test]: 2) no change', prevState);
            return prevState;
        })
    }, [remoteParticipants]);

    const memoizedRemoteParticipants = useMemo(
        () => {
            if (mockedParticipants.length > 0) {
                return [...remoteParticipantsInternal, ...mockedParticipants];
            }
            return remoteParticipantsInternal;
        },
        [remoteParticipantsInternal, mockedParticipants]
    );
    const prioritizedParticipants = useMemo(
        () => memoizedRemoteParticipants.filter(isUnmutedOrRaisedHand).sort((a, b) => {
            // sort:
            // 1. unmuted participants first
            // 2. raisedHand participants ordered by raisedHandOrderPosition
            if (a.isMuted && !b.isMuted) return 1;
            if (!a.isMuted && b.isMuted) return -1;
            if (a.raisedHand && b.raisedHand) {
                return a.raisedHand.raisedHandOrderPosition - b.raisedHand.raisedHandOrderPosition;
            }

            return 0;
        }),
        [memoizedRemoteParticipants]
    );

    const unprioritizedParticipants = useMemo(
        () => memoizedRemoteParticipants.filter(p => !prioritizedParticipants.includes(p)),
        [memoizedRemoteParticipants, prioritizedParticipants]
    );
    const numParticipantsToFillUp = useMemo(
        () => participantsPerPage - prioritizedParticipants.length,
        [prioritizedParticipants, participantsPerPage]
    );

    useEffect(() => {
        setGalleryNumPages(Math.ceil(memoizedRemoteParticipants.length / participantsPerPage));
    }, [memoizedRemoteParticipants]);

    const participantsToShow = useMemo(() => {
        const start = Math.max(0, galleryPage * participantsPerPage - prioritizedParticipants.length);
        const end = start + numParticipantsToFillUp;
        const participantsForPage = unprioritizedParticipants.slice(start, end);

        // participants for the current page
        // list prioritized participants first
        const participantsToShow = prioritizedParticipants.concat(participantsForPage).slice(0, participantsPerPage);

        const hiddenParticipants = memoizedRemoteParticipants.filter(p => !participantsToShow.includes(p));
        hiddenParticipants.forEach(p => adapter.disposeRemoteVideoStreamView(p.userId));

        return participantsToShow.map(p => {
            const participant = remoteParticipants.find(p2 => p2.userId === p.userId);
            if (!participant) {
                return p;
            }

            return participant;
        });
    }, [
        memoizedRemoteParticipants,
        galleryPage,
        numParticipantsToFillUp,
        prioritizedParticipants,
        unprioritizedParticipants,
        participantsPerPage,
        remoteParticipants,
        mockedParticipants,
    ]);

    return {
        galleryPage,
        galleryNumPages,
        hasMultiplePages,
        participantsToShow,

        goToPrevPage,
        goToNextPage,

        hasPrevPage,
        hasNextPage,
    };
};

export const toPcpGalleryPagerProps = (props: {
    galleryPage: number,
    galleryNumPages: number,
    hasMultiplePages: boolean,
    goToPrevPage: () => void,
    goToNextPage: () => void,
    hasPrevPage: boolean,
    hasNextPage: boolean,
}): PcpGalleryPagerProps => ({
        page: props.galleryPage,
        numPages: props.galleryNumPages,
        hasMultiplePages: props.hasMultiplePages,
        goToPrevPage: props.goToPrevPage,
        goToNextPage: props.goToNextPage,
        hasPrevPage: props.hasPrevPage,
        hasNextPage: props.hasNextPage
    })
